import { Box, Container, Grid, Typography, IconButton, Link as MuiLink, useTheme, useMediaQuery } from "@mui/material";
import theme from "../../theme";
import InstagramIcon from '@mui/icons-material/Instagram';
import FacebookIcon from '@mui/icons-material/Facebook';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

export default function Footer() {
    const { t } = useTranslation();
    const muiTheme = useTheme();
    const isMobile = useMediaQuery(muiTheme.breakpoints.down('sm'));

    const socialLinks = [
        { icon: InstagramIcon, url: "https://www.instagram.com/laboria_recruitment", label: "Instagram" },
        { icon: FacebookIcon, url: "https://www.facebook.com/profile.php?id=61573067913619", label: "Facebook" },
        { icon: LinkedInIcon, url: "https://www.linkedin.com/company/laboria-llc/about/?viewAsMember=true", label: "LinkedIn" }
    ];

    return (
        <Box
            component="footer"
            sx={{
                backgroundColor: theme.palette.background.default,
                color: theme.palette.primary.main,
                py: isMobile ? 4 : 6,
                // Use normal document flow instead of positioning
                width: '100%',
                // Add top border for visual separation
                borderTop: `1px solid ${theme.palette.primary.main}20`,
                '& a': {  // Override global styles
                    color: `${theme.palette.primary.main} !important`,
                    '&:hover': {
                        color: `${theme.palette.primary.dark} !important`,
                    }
                }
            }}
        >
            <Container maxWidth="lg">
                <Grid container spacing={isMobile ? 3 : 4}>
                    {/* Company Info */}
                    <Grid item xs={12} sm={4}>
                        <Typography 
                            variant={isMobile ? "h6" : "h6"} 
                            gutterBottom 
                            sx={{ color: theme.palette.primary.main }}
                        >
                            Laboria Inc
                        </Typography>
                        <Typography 
                            variant={isMobile ? "body2" : "body2"} 
                            sx={{ color: theme.palette.primary.main }}
                        >
                            {t('footer.companyDescription', 'Connecting talent with opportunities')}
                        </Typography>
                        <Typography 
                            variant={isMobile ? "body2" : "body2"} 
                            sx={{ 
                                color: theme.palette.primary.main,
                                mt: 1
                            }}
                        >
                            <MuiLink 
                                href="mailto:info@laboria.work" 
                                sx={{ 
                                    textDecoration: 'none',
                                    '&:hover': { 
                                        textDecoration: 'underline'
                                    }
                                }}
                            >
                                info@laboria.work
                            </MuiLink>
                        </Typography>
                        <Typography 
                            variant={isMobile ? "body2" : "body2"} 
                            sx={{ 
                                color: theme.palette.primary.main,
                                mt: 1
                            }}
                        >
                            <MuiLink 
                                href="tel:+18882129369" 
                                sx={{ 
                                    textDecoration: 'none',
                                    '&:hover': { 
                                        textDecoration: 'underline'
                                    }
                                }}
                            >
                                +1 888-212-9369
                            </MuiLink>
                        </Typography>
                        <Typography 
                            variant={isMobile ? "body2" : "body2"} 
                            sx={{ 
                                color: theme.palette.primary.main,
                                mt: 1
                            }}
                        >
                            66 W Flagler Street, Suite 900, PMB 11825<br />
                            Miami, FL 33130
                        </Typography>
                        <Typography 
                            variant={isMobile ? "body2" : "body2"} 
                            sx={{ 
                                color: theme.palette.primary.main,
                                mt: 1
                            }}
                        >
                            {t('footer.businessHours', 'Business Hours: Monday to Friday, 8 AM - 5 PM (Honduras Central America Time)')}
                        </Typography>
                    </Grid>

                    {/* Quick Links */}
                    <Grid item xs={12} sm={4}>
                        <Typography 
                            variant={isMobile ? "h6" : "h6"} 
                            gutterBottom 
                            sx={{ color: theme.palette.primary.main }}
                        >
                            {t('footer.quickLinks', 'Quick Links')}
                        </Typography>
                        <Box display="flex" flexDirection="column" gap={1}>
                            <MuiLink 
                                component={Link} 
                                to="/" 
                                sx={{ 
                                    textDecoration: 'none',
                                    fontSize: isMobile ? '0.875rem' : '1rem',
                                    '&:hover': { 
                                        textDecoration: 'underline'
                                    }
                                }}
                            >
                                {t('nav.home', 'Home')}
                            </MuiLink>
                            <MuiLink 
                                component={Link} 
                                to="/about" 
                                sx={{ 
                                    textDecoration: 'none',
                                    fontSize: isMobile ? '0.875rem' : '1rem',
                                    '&:hover': { 
                                        textDecoration: 'underline'
                                    }
                                }}
                            >
                                {t('nav.about', 'About')}
                            </MuiLink>
                            <MuiLink 
                                component={Link} 
                                to="/contact" 
                                sx={{ 
                                    textDecoration: 'none',
                                    fontSize: isMobile ? '0.875rem' : '1rem',
                                    '&:hover': { 
                                        textDecoration: 'underline'
                                    }
                                }}
                            >
                                {t('nav.contact', 'Contact')}
                            </MuiLink>
                        </Box>
                    </Grid>

                    {/* Social Links */}
                    <Grid item xs={12} sm={4}>
                        <Typography 
                            variant={isMobile ? "h6" : "h6"} 
                            gutterBottom 
                            sx={{ color: theme.palette.primary.main }}
                        >
                            {t('footer.followUs', 'Follow Us')}
                        </Typography>
                        <Box display="flex" gap={2} justifyContent={isMobile ? "center" : "flex-start"}>
                            {socialLinks.map((social) => (
                                <IconButton
                                    key={social.label}
                                    component="a"
                                    href={social.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    aria-label={social.label}
                                    size={isMobile ? "small" : "medium"}
                                    sx={{
                                        '&:hover': {
                                            transform: 'scale(1.1)',
                                        },
                                        transition: 'all 0.2s ease-in-out'
                                    }}
                                >
                                    <social.icon />
                                </IconButton>
                            ))}
                        </Box>
                    </Grid>
                </Grid>

                {/* Copyright */}
                <Box
                    sx={{
                        borderTop: 1,
                        borderColor: theme.palette.primary.main,
                        borderOpacity: 0.1,
                        mt: isMobile ? 3 : 4,
                        pt: isMobile ? 2 : 3,
                        textAlign: 'center'
                    }}
                >
                    <Typography 
                        variant={isMobile ? "caption" : "body2"} 
                        sx={{ color: theme.palette.primary.main }}
                    >
                        &copy; {new Date().getFullYear()} Laboria. {t('footer.allRightsReserved', 'All rights reserved.')}
                    </Typography>
                </Box>
            </Container>
        </Box>
    );
}